import { AppPluginDef, AppSettingsDef } from "../app";
import { BarcodeScannerDef } from "../barcode";
import { MobileDeviceCameraDef } from "../camera";
import { MobileDeviceInfoDef, MobileDeviceSystemBars } from "../device";
import { VirtualKeyboardDef } from "../keyboard";
import { OrientationAccessorDef } from "@mcleod/core";
import { SplashScreenDef } from "../splash";

/**
 * Interface representing the Capacitor global object.
 */
interface Capacitor {
    Plugins?: CapacitorPlugins;
    isNativePlatform: () => boolean;
    getPlatform: () => string;
}

/**
 * Minimal shape of the native OTA plugin used by this library.
 *
 * The full OTA plugin is defined in the higher-level mcleod-ui-mobile connect shell;
 * only reloadBuiltIn is needed here, so we declare just that method to avoid a
 * dependency from this shared library up to that package.
 */
export interface OTAPluginDef {
    /** Reload the WebView with the built-in shell bundle (used on logout / token clear / connection failure). */
    reloadBuiltIn(): Promise<void>;
}

/**
 * Interface representing available Capacitor plugins.
 * Add new plugin interfaces here as they are integrated.
 */
interface CapacitorPlugins {
    App?: AppPluginDef;
    CapacitorBarcodeScanner?: BarcodeScannerDef;
    Device?: MobileDeviceInfoDef,
    SystemBars?: MobileDeviceSystemBars;
    AppSettings?: AppSettingsDef;
    ScreenOrientation?: OrientationAccessorDef;
    Camera?: MobileDeviceCameraDef;
    Keyboard?: VirtualKeyboardDef;
    SplashScreen?: SplashScreenDef;
    OTAPlugin?: OTAPluginDef;
}

/**
 * Provides access to Capacitor and its plugins.
 * This class handles the detection and retrieval of the Capacitor instance
 * from the window object, similar to how it's done in MobileDeviceAccessorDef.
 */
export class CapacitorAccess {
    /**
     * Gets the Capacitor instance from window or parent window.
     * Returns undefined if Capacitor is not available.
     */
    public static get instance(): Capacitor | undefined {
        return window["Capacitor"] ?? window.parent?.["Capacitor"];
    }

    /**
     * Gets the Capacitor plugins object.
     * Returns undefined if Capacitor or plugins are not available.
     */
    public static get plugins(): CapacitorPlugins | undefined {
        return this.instance?.Plugins;
    }

    /**
     * Returns true if running as a native app (not in browser).
     */
    public static get isNativePlatform(): boolean {
        return this.instance?.isNativePlatform() ?? false;
    }

    /**
     * Gets the current platform ('ios', 'android', or 'web').
     */
    public static get platform(): string | undefined {
        return this.instance?.getPlatform();
    }

    public static get isAndroid(): boolean {
        return this.platform === "android";
    }

    public static get isIOS(): boolean {
        return this.platform === "ios";
    }
}
